import { useEffect, useState, type MouseEvent } from 'react';
import { Loader2 } from 'lucide-react';

import { cn } from 'lib/utils';
import type { MentionSearchType } from '../../types/mentions.service.type';
import type { MentionPickerItem } from 'features/chat/utils/normalize-mention-search';
import {
  formatMentionTypeLabel,
  getMentionCategoryIcon,
  getMentionCategoryLabel,
} from './mention-picker.constants';

type MentionCategoryItemsMenuProps = {
  /** When omitted, shows combined search results (skills + prompts). */
  category?: MentionSearchType | null;
  items: MentionPickerItem[];
  isLoading: boolean;
  query: string;
  onSelect: (item: MentionPickerItem) => void;
  onClearFilter?: () => void;
};

function itemKey(item: MentionPickerItem): string {
  return `${item.type}:${item.id}`;
}

function selectItem(
  event: MouseEvent,
  item: MentionPickerItem,
  onSelect: (item: MentionPickerItem) => void
) {
  event.preventDefault();
  event.stopPropagation();
  onSelect(item);
}

function CategoryIcon({ type, className }: { type: MentionSearchType; className?: string }) {
  const Icon = getMentionCategoryIcon(type);
  return <Icon className={cn('h-4 w-4 shrink-0 text-primary', className)} />;
}

function truncatePreview(text: string, max = 48): string {
  const preview = text.replace(/\s+/g, ' ').trim();
  return preview.length > max ? `${preview.slice(0, max)}…` : preview;
}

function itemStatusLabel(item: MentionPickerItem, isSearchMode: boolean): string | null {
  if (item.status?.trim()) return item.status.trim();
  if (isSearchMode) return formatMentionTypeLabel(item.type);
  if (item.body?.trim()) return truncatePreview(item.body);
  return null;
}

export function MentionCategoryItemsMenu({
  category = null,
  items,
  isLoading,
  query,
  onSelect,
  onClearFilter,
}: MentionCategoryItemsMenuProps) {
  const [focusedKey, setFocusedKey] = useState<string | null>(null);
  const isSearchMode = !category;
  const headerLabel = category ? getMentionCategoryLabel(category) : 'Mentions';
  const emptyLabel = category ? getMentionCategoryLabel(category).toLowerCase() : 'results';
  const showClearFilter = Boolean(query || category);

  useEffect(() => {
    if (items.length > 0) {
      setFocusedKey((current) =>
        current && items.some((item) => itemKey(item) === current) ? current : itemKey(items[0])
      );
    } else {
      setFocusedKey(null);
    }
  }, [items]);

  return (
    <div className="w-full max-w-[220px] overflow-hidden rounded-xl border border-border/60 bg-popover shadow-lg">
      <div className="flex items-center justify-between border-b border-border/50 px-3 py-2.5">
        <span className="text-sm font-medium text-muted-foreground">{headerLabel}</span>
        {showClearFilter ? (
          <button
            type="button"
            className="text-xs text-muted-foreground transition-colors hover:text-foreground"
            onMouseDown={(event) => {
              event.preventDefault();
              event.stopPropagation();
              onClearFilter?.();
            }}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
            }}
          >
            Clear filter
          </button>
        ) : null}
      </div>

      <div className="max-h-[240px] overflow-y-auto py-1">
        {isLoading ? (
          <div className="flex items-center justify-center gap-2 px-3 py-8 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading...
          </div>
        ) : items.length === 0 ? (
          <p className="px-3 py-6 text-center text-sm text-muted-foreground">
            {query ? `No ${emptyLabel} match your search` : `No ${emptyLabel} found`}
          </p>
        ) : (
          items.map((item) => {
            const key = itemKey(item);
            const isFocused = key === focusedKey;
            const status = itemStatusLabel(item, isSearchMode);

            return (
              <button
                key={key}
                type="button"
                className={cn(
                  'flex w-full items-center gap-2.5 px-3 py-2.5 text-left text-sm transition-colors',
                  isFocused ? 'bg-muted' : 'hover:bg-muted/60'
                )}
                onMouseEnter={() => setFocusedKey(key)}
                onMouseDown={(event) => selectItem(event, item, onSelect)}
              >
                <CategoryIcon type={item.type} className="dark:text-true-white" />
                <span className="min-w-0 flex-1 truncate font-medium text-foreground">{item.title}</span>
                {status ? (
                  <span className="max-w-[40%] shrink-0 truncate text-xs text-muted-foreground">{status}</span>
                ) : null}
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}
